import axios from 'axios/dist/axios';

const userRoles = {
    USER_ADMIN: "USER_ADMIN",
    USER: "USER",
};

const appTypes = {
    PF: "PF",                               // Pathfinder.
    PF_CHATBOT: "PF_CHATBOT",               // Pathfinder - Chat Bot.
    PF_CHATBOT_STUDY: "PF_CHATBOT_STUDY",   // Pathfinder - Chat Bot - study mode.
    PRO: "PRO",                             // Professional.
    PRO_PRIORITY: "PRO_PRIORITY",            // Professional - Priority Mode.
    PF_CHATBOT_PUBLIC: "PF_CHATBOT_PUBLIC",  // Pathfinder - PF_CHATBOT_PUBLIC
};

const defaultState = {
    userData: {},

    accountData: {}, // User account data, used for User Account page.
    countryList: [
        // { name: "Switzerland", key: 1 },
        // { name: "Germany", key: 2 },
        // { name: "Austria", key: 3 },
        // { name: "Italy", key: 4 }
    ],
    salutationList: [],

    currentAppType: null,

    debug: {
        mustShowCodes: false
    },

};


// ======================================================================
// Initial State
// ======================================================================

const state = {
    userData: defaultState.userData,
    accountData: defaultState.accountData,
    countryList: defaultState.countryList,
    salutationList: defaultState.salutationList,
    currentAppType: defaultState.currentAppType,

    debug: {
        mustShowCodes: defaultState.debug.mustShowCodes
    }
};


// ======================================================================
// Getters
// ======================================================================

const getters = {
    getUserData: state => state.userData,
    getAccountData: state => state.accountData,
    getCountryList: state => state.countryList,
    getSalutationList: state => state.salutationList,
    getEmailAddress (state) {
        return (typeof state.userData.emailAddress !== "undefined" ? state.userData.emailAddress : "");
    },
    getFullName (state) {
        let firstName = typeof state.userData.firstName !== "undefined" ? state.userData.firstName : "";
        let lastName = typeof state.userData.name !== "undefined" ? state.userData.name : "";
        let separator = ((firstName !== "" && lastName !== "") ? " " : "");
        return (firstName + separator + lastName);
    },
    getUsername (state) {
        return (typeof state.userData.username !== "undefined" ? state.userData.username : "");
    },

    getIsUserLoggedIn () {
        return (state.userData !== null
            && state.userData.constructor === Object
            && Object.keys(state.userData).length > 0) ? true : false;
    },

    getRole (state) {
        return (typeof state.userData.role !== "undefined" ? state.userData.role : null);
    },
    getIsAdmin (state) {
        return (typeof state.userData.role !== "undefined" && state.userData.role === userRoles.USER_ADMIN) ? true : false;
    },

    getAppTypes: () => appTypes,
    getCurrentAppType: state => state.currentAppType,

    getDebugMustShowCodes(state, getters) {
        return (getters.getIsAdmin) ? state.debug.mustShowCodes : false;
    }
};


// ======================================================================
// Actions
// ======================================================================

const actions = {

    /**
     * Authenticate the user.
     * @param {Object} - Vuex context: commit, dispatch
     * @param {Object} loginFormData - Object containing the following properties: "username", "password".
     * @return {Promise}
     */
    submitLogin({ commit, dispatch }, loginFormData) {

        let promise = new Promise((resolve, reject) => {
            let data = {
                username: loginFormData.username,
                password: loginFormData.password
            };

            let autologin = loginFormData.autologin;
            let loginRequest;

            if(typeof autologin !== 'undefined' && autologin === true ){
                data.loginMethod = "get";
                loginRequest = axios.post(process.env.API_BASE_URL_AUTH + '/autoLogin', data);
            }
            else{
                data.loginMethod = "post";
                loginRequest = axios.post(process.env.API_BASE_URL_AUTH + '/login', data);
            }


            loginRequest.then(response => {
                if(typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) { // returnCode=0 means success.
                    if (typeof response.data.userData !== "undefined") {
                        commit("setUserData", response.data.userData);
                    }

                    if (typeof response.data.appProperties !== "undefined") {
                        commit("TranslationStore/setAppProperties", response.data.appProperties, {root: true});
                        commit("TranslationStore/setAppTypeName", response.data.appProperties.APPLICATION_TYPE, {root: true});
                        dispatch("SymptomsQuestionsStore/updateAssessmentTypeByAppProp", null, {root: true});
                    }

                    // Set (or update, if already set) user specific language settings.
                    if (typeof response.data.languages !== "undefined") {
                        commit("TranslationStore/setLanguageList", response.data.languages, {root: true});
                    }

                    if (typeof response.data.selectedLanguage !== "undefined") {
                        commit('TranslationStore/setSelectedLanguage', response.data.selectedLanguage, {root: true});
                        this._vm.$UtilitiesPlugin.setCookie('language', response.data.selectedLanguage, 366);
                    }

                    if (typeof response.data.texts !== "undefined") {
                        commit('TranslationStore/setTexts', response.data.texts, {root: true});
                    }

                    resolve(response);
                }
                else {
                    console.log("Login failed.");

                    let loginFailMsg = "Login failed. Username or password may be wrong.";
                    reject(loginFailMsg);
                }
            });
            loginRequest.catch(error => {
                console.log("Login error: ", error);
                reject(error);
            });
        });

        return promise;
    },


    /**
     * Delete a session from server.
     * @return {Promise}
     */
    submitLogout({ commit }) {
        let promise = new Promise((resolve, reject) => {
            let logoutRequest = axios.post(process.env.API_BASE_URL_LOGOUT + '/logoutPro');
            logoutRequest.then(response => {
                if(typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) { // returnCode=0 means success.
                    commit("resetStore");
                    resolve(response);
                }
                else {
                    reject("Logout failed.");
                }
            });
            logoutRequest.catch(error => {
                console.log("Logout error: ", error);
                reject(error);
            });
        });

        return promise;
    },


    loadAccountData({ commit }) {
        let promise = new Promise((resolve, reject) => {
            let axiosRequest = axios.post(process.env.API_BASE_URL + '/retrieveUser');
            axiosRequest.then((response) => {

                if (typeof response.data.userData !== "undefined") {
                    commit('setAccountData', response.data.userData);
                }

                if (typeof response.data.countryList !== "undefined") {
                    commit('setCountryList', response.data.countryList);
                }

                if (typeof response.data.salutationMap !== "undefined") {
                    commit('setSalutationList', response.data.salutationMap);
                }

                resolve(response);
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },

    loadAdditionalUserPageData({ commit }) {
        let promise = new Promise((resolve, reject) => {
            let axiosRequest = axios.post(process.env.API_BASE_URL + '/additionalUserPageData');
            axiosRequest.then((response) => {
                if (typeof response.data.countryList !== "undefined") {
                    commit('setCountryList', response.data.countryList);
                }
                if (typeof response.data.salutationMap !== "undefined") {
                    commit('setSalutationList', response.data.salutationMap);
                }

                resolve(response);
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },


    /**
     * Submit forgot password form.
     * @param {Object} - Vuex: commit
     * @param {Object} formData - Object containing the following properties: "username".
     * @return {Promise}
     */
    submitForgotPassword({ commit }, formData) {

        let promise = new Promise((resolve, reject) => {

            let forgotPassRequest = axios.post(process.env.API_BASE_URL_AUTH + '/forgotPassword', formData);
            forgotPassRequest.then(response => {
                if(typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) { // returnCode=0 means success.
                    resolve(response);
                }
                else {
                    console.log("Failed.");
                    reject("Forgot password form submission failed.");
                }
            });
            forgotPassRequest.catch(error => {
                console.log("Forgot password error: ", error);
                reject(error);
            });
        });

        return promise;
    },

    checkResetPasswordAccess(context, requestParams) {

        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL + '/resetPasswordAccess', requestParams);

            axiosRequest.then((response) => {
                // If returnCode is not 0, the axios interceptor from AjaxHelperPlugin.js throws an error, so we
                // don't need to check here the value of `returnCode`, because this code will never run if the value
                // of `returnCode is other than 0. In that case, it will run only the code from `catch` function.

                // SUCCESS
                resolve(response);
            });
            axiosRequest.catch((error) => {
                // Ajax request failure.
                this.isAccessError = true;
                console.log(error);
                reject(error);
            });

        });

        return promise;
    },

    submitChangeUserPassword({ commit }, params) {

        let promise = new Promise((resolve, reject) => {

            let changePassRequest = axios.post(process.env.API_BASE_URL_AUTH + '/resetPassword', params);
            changePassRequest.then(response => {
                // if(typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) { // returnCode=0 means success.
                resolve(response);
                // }
                // else {
                //     console.log("Change password failed.");
                //     reject("Change password failed.");
                // }
            });
            changePassRequest.catch(error => {
                console.log("Change password error: ", error);
                reject(error);
            });
        });

        return promise;
    },


    submitUserRegistration ({ commit }, formData) {
        let promise = new Promise((resolve, reject) => {

            let userRegistrationRequest = axios.post(process.env.API_BASE_URL_AUTH + '/registerUser', formData);
            userRegistrationRequest.then(response => {
                if(typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) { // returnCode=0 means success.
                    if (typeof response.data.userData !== "undefined") {
                        commit("setUserData", response.data.userData);
                    }

                    resolve(response);
                }
                else {
                    console.log("User registration failed.");

                    let userRegFailMsg = "User registration failed.";
                    reject(userRegFailMsg);
                }
            });
            userRegistrationRequest.catch(error => {
                console.log("User registration error: ", error);
                reject(error);
            });
        });

        return promise;
    },


    submitChangeUserAccount ({ commit }, formData) {
        let promise = new Promise((resolve, reject) => {

            let accountUpdateRequest = axios.post(process.env.API_BASE_URL_AUTH + '/updateUser', formData);
            accountUpdateRequest.then(response => {
                if(typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) { // returnCode=0 means success.
                    resolve(response);
                }
                else {
                    console.log("User account update failed.");

                    reject(response);
                }
            });
            accountUpdateRequest.catch(error => {
                console.log("User account update error: ", error);
                reject(error);
            });
        });

        return promise;
    },
    submitChangeUserPasswordAccount ({ commit }, formData) {
        let promise = new Promise((resolve, reject) => {

            let accountUpdateRequest = axios.post(process.env.API_BASE_URL_AUTH + '/updatePassword', formData);
            accountUpdateRequest.then(response => {
                if(typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) { // returnCode=0 means success.
                    resolve(response);
                } else if(typeof response.data.returnCode !== "undefined" && response.data.returnCode === 112) { // password from database not match with password written
                    console.log("User account update failed.");

                    let accountUpdateFailMsg = "Old password is wrong.";
                    reject(accountUpdateFailMsg);
                }
                else {
                    console.log("User account update failed.");

                    let accountUpdateFailMsg = "User account update failed.";
                    reject(accountUpdateFailMsg);
                }
            });
            accountUpdateRequest.catch(error => {
                console.log("User account update error: ", error);
                reject(error);
            });
        });

        return promise;
    },

};


// ======================================================================
// Mutations
// ======================================================================

const mutations = {

    setUserData (state, data) {
        state.userData = (data !== null) ? data : {};
    },
    setAccountData (state, data) {
        state.accountData = (data !== null) ? data : {};
    },
    setItemsInAccountData (state, data) {
        // Add "data" object properties to "state.accountData" object. If a property from "data" already exists
        // on "state.accountData", override it with the value from "data". So "data" values will be kept and "state.accountData" values will be lost.
        Object.assign(state.accountData, data);
    },

    setEmailAddress (state, emailAddress) {
        state.userData.emailAddress = emailAddress;
    },
    setRole (state, role) {
        state.userData.role = role;
    },

    setCountryList (state, data) {
        state.countryList = (data !== null) ? data : {};
    },
    setSalutationList (state, data) {
        state.salutationList = (data !== null) ? data : {};
    },

    setCurrentAppType (state, newValue) {
        state.currentAppType = newValue;
    },

    setDebugMustShowCodes (state, mustShowCodes) {
        state.debug.mustShowCodes = mustShowCodes;
    },


    // ----------------------------------------------------------

    resetStore (state) {
        state.userData = defaultState.userData;
        state.userAccount = defaultState.userAccount;
        state.countryList = defaultState.countryList;

        state.debug = {
            mustShowCodes: defaultState.debug.mustShowCodes
        };
    }
};


// ======================================================================
// Exports
// ======================================================================

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};



// WEBPACK FOOTER //
// ./src/common/store/modules/UserStore.js